import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useCatalog } from "@/lib/useCatalog";
import { api, apiError, BACKEND_URL } from "@/lib/api";
import { money, MODE_META } from "@/lib/pricing";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { TipSelector, tipAmount } from "@/components/TipSelector";
import { Trash2, Truck, Store, ArrowRight, ShoppingBag, Clock, CalendarX2 } from "lucide-react";
import { toast } from "sonner";

const DELIVERY_WINDOWS = ["8:00–10:00 AM", "8:00–10:00 PM"];
export const DELIVERY_POLICY = "I understand that the driver cannot buzz up or deliver upstairs. I must meet the driver at the front door or designated vehicle accessible pickup point to receive my order.";

const PickupSlotPicker = ({ times, value, onChange }) => (
  <div className="mt-4">
    <label className="mb-1.5 block text-sm font-bold text-sugar-ink"><Clock size={15} className="mr-1 inline" /> Contactless pickup time (9 AM – 8 PM)</label>
    <div className="flex flex-wrap gap-2">
      {times.map((s) => (
        <button key={s.v} type="button" data-testid={`pre-pickup-${s.v}`} onClick={() => onChange(s.v)} className={`rounded-full border px-3.5 py-2 text-sm font-semibold ${value === s.v ? "border-sugar-pink bg-sugar-pink text-white" : "border-sugar-border bg-white text-sugar-ink2 hover:border-sugar-pink/50"}`}>
          {s.label}
        </button>
      ))}
    </div>
    <p className="mt-2 rounded-xl border border-sugar-border bg-sugar-blush p-3 text-xs font-medium text-sugar-ink2">You'll receive a confirmation text within 24 hours to confirm your pickup time.</p>
  </div>
);

const CartPhotos = ({ rows, idx }) => {
  const photos = rows.filter((r) => r.image);
  if (!photos.length) return null;
  return (
    <div data-testid={`cart-item-photos-${idx}`} className="mt-3">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-sugar-muted">Your photos</p>
      <div className="mt-1.5 flex flex-wrap gap-2">
        {photos.map((p, j) => (
          <a key={j} href={p.image} target="_blank" rel="noreferrer" title={p.label} data-testid={`cart-item-${idx}-photo-${j}`}>
            <img src={p.image} alt={p.label} className="h-14 w-14 rounded-lg border border-sugar-border object-cover transition-transform hover:scale-105" />
          </a>
        ))}
      </div>
    </div>
  );
};

export default function Checkout() {
  const { user, checked } = useAuth();
  const navigate = useNavigate();
  const cart = useCart();
  const catalog = useCatalog();
  const isQuick = cart.mode === "same_day" || cart.mode === "next_day";
  const isSameDay = cart.mode === "same_day";
  const isPreOrder = cart.mode === "pre_order";

  const [method, setMethod] = useState("delivery");
  const [zone, setZone] = useState("langley");
  const [form, setForm] = useState({ customer_name: "", customer_phone: "", address: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [orderInfo, setOrderInfo] = useState(null);
  const [pickupTime, setPickupTime] = useState("");
  const [deliveryTime, setDeliveryTime] = useState("");
  const [tip, setTip] = useState({ type: "none", pct: 0, custom: "" });
  const [agreePolicy, setAgreePolicy] = useState(false);
  const [blockedDates, setBlockedDates] = useState([]);

  useEffect(() => {
    api.get("/availability").then(({ data }) => setBlockedDates(data.blocked_dates || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (checked && !user) navigate("/login", { state: { next: "/checkout" } });
    if (user) setForm((f) => ({ ...f, customer_name: f.customer_name || user.name, customer_phone: f.customer_phone || user.phone }));
  }, [checked, user, navigate]);

  useEffect(() => {
    if (isQuick) setMethod("pickup");
  }, [isQuick]);

  useEffect(() => {
    if (isSameDay) api.get("/ordering-info").then(({ data }) => setOrderInfo(data)).catch(() => {});
  }, [isSameDay]);

  const fmtTime = (m) => {
    const h = Math.floor(m / 60), mm = m % 60;
    const ap = h < 12 ? "AM" : "PM";
    const h12 = ((h + 11) % 12) + 1;
    return `${h12}:${String(mm).padStart(2, "0")} ${ap}`;
  };
  const to24 = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

  const slots = useMemo(() => {
    if (!isSameDay || !orderInfo) return [];
    const earliest = Math.ceil((orderInfo.now_minutes + orderInfo.same_day_min_minutes) / 30) * 30;
    const end = 20 * 60;
    const arr = [];
    for (let m = earliest; m <= end; m += 30) {
      arr.push({ v: to24(m), label: fmtTime(m), rush: m < orderInfo.now_minutes + orderInfo.same_day_rush_minutes });
    }
    return arr;
  }, [isSameDay, orderInfo]);

  const pickupTimes = useMemo(() => {
    const arr = [];
    for (let m = 9 * 60; m <= 20 * 60; m += 30) arr.push({ v: to24(m), label: fmtTime(m) });
    return arr;
  }, []);

  const selectedSlot = slots.find((s) => s.v === pickupTime);
  const rushFee = isSameDay && selectedSlot && selectedSlot.rush ? (orderInfo?.rush_fee || 50) : 0;

  if (!catalog) return (<div className="App"><Navbar /><div className="py-32 text-center text-sugar-ink2">Loading…</div></div>);

  const zones = catalog.delivery_zones;
  const freeThreshold = catalog.free_threshold;
  const subtotal = cart.subtotal;

  const freeZone = ["pickup", "langley", "surrey"].includes(zone);
  const zoneObj = zones.find((z) => z.id === zone) || { fee: 0 };
  let deliveryFee = 0;
  if (method === "delivery" && !freeZone && subtotal < freeThreshold) deliveryFee = zoneObj.fee;

  const requireDeliveryTime = method === "delivery";
  const needsPickupSlots = method === "pickup" && !isSameDay;
  const dateBlocked = blockedDates.includes(cart.date);

  const tax = Math.round((subtotal + deliveryFee + rushFee) * 0.12 * 100) / 100;
  const tipValue = tipAmount(tip, subtotal);
  const total = Math.round((subtotal + deliveryFee + rushFee + tax + tipValue) * 100) / 100;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const styleById = (id) => (catalog.styles || []).find((s) => s.id === id);
  const detailRows = (item) => {
    const style = styleById(item.style_id);
    if (!style) return [];
    const rows = [];
    for (const grp of style.groups) {
      const val = item.selections[grp.id];
      if (val == null || val === "" || (Array.isArray(val) && !val.length)) continue;
      if (grp.type === "image") { rows.push({ label: grp.label, value: "Photo attached", image: `${BACKEND_URL}${val}` }); continue; }
      if (grp.type === "images") { (Array.isArray(val) ? val : []).forEach((u, i) => rows.push({ label: `${grp.label} ${i + 1}`, value: "Photo attached", image: `${BACKEND_URL}${u}` })); continue; }
      if (grp.multi) {
        (Array.isArray(val) ? val : []).forEach((v) => {
          const o = grp.options.find((x) => x.value === v);
          rows.push({ label: grp.label, value: o ? o.label : v, addon: true });
        });
      } else {
        const opts = grp.options_map ? Object.values(grp.options_map).flat() : grp.options;
        const o = opts.find((x) => x.value === val);
        rows.push({ label: grp.label, value: o ? o.label : val });
      }
    }
    return rows;
  };

  const placeOrder = async () => {
    if (!form.customer_name || !form.customer_phone) { toast.error("Please add your name & phone"); return; }
    if (method === "delivery" && !form.address) { toast.error("Please add a delivery address"); return; }
    if (dateBlocked) { toast.error("Sorry, this date is unavailable. Please choose another date."); return; }
    if (method === "pickup" && !pickupTime) { toast.error("Please choose a pickup time"); return; }
    if (requireDeliveryTime && !DELIVERY_WINDOWS.includes(deliveryTime)) { toast.error("Please choose a delivery window (8–10 AM or 8–10 PM)"); return; }
    if (method === "delivery" && !agreePolicy) { toast.error("Please agree to the delivery policy to continue"); return; }
    if (tip.type === "custom" && (tip.custom === "" || Number(tip.custom) < 0)) { toast.error("Please enter a tip amount, or choose No tip"); return; }
    setLoading(true);
    try {
      const payload = {
        mode: cart.mode,
        fulfillment_date: cart.date,
        items: cart.items.map((it) => ({ style_id: it.style_id, selections: it.selections })),
        delivery_method: method,
        delivery_zone: method === "pickup" ? "pickup" : zone,
        customer_name: form.customer_name,
        customer_phone: form.customer_phone,
        address: form.address,
        notes: form.notes,
        pickup_time: method === "pickup" ? pickupTime : "",
        delivery_time: method === "delivery" ? deliveryTime : "",
        tip_type: tip.type,
        tip_percent: tip.pct,
        tip_amount: Number(tip.custom) || 0,
        delivery_policy_agreed: method === "delivery" && agreePolicy,
      };
      const { data: order } = await api.post("/orders", payload);
      const { data: checkout } = await api.post("/payments/checkout", { order_id: order.id, origin_url: window.location.origin });
      cart.clear();
      window.location.href = checkout.checkout_url;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
      setLoading(false);
    }
  };

  if (cart.items.length === 0) {
    return (
      <div className="App"><Navbar />
        <div className="mx-auto max-w-md px-6 py-24 text-center">
          <span className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-sugar-blush text-sugar-pink"><ShoppingBag size={28} /></span>
          <h1 className="mt-5 font-display text-2xl font-bold text-sugar-ink">Your cart is empty</h1>
          <p className="mt-2 text-sugar-ink2">Let's build something sweet.</p>
          <Button data-testid="empty-cart-order-btn" onClick={() => navigate("/order")} className="mt-6 rounded-full bg-sugar-pink px-8 py-6 font-bold text-white hover:bg-sugar-pinkhover">Start an order</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="App min-h-screen bg-sugar-bg"><Navbar />
      <div className="mx-auto grid max-w-6xl gap-8 px-6 py-10 lg:grid-cols-[1fr_400px] lg:px-8">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-sugar-ink">Checkout</h1>
          <p className="mt-1 text-sugar-ink2">{MODE_META[cart.mode]?.label} · {cart.date}</p>

          {/* items */}
          <div className="mt-6 space-y-3">
            {cart.items.map((it, i) => (
              <div key={i} data-testid={`cart-item-${i}`} className="flex gap-4 rounded-2xl border border-sugar-border bg-white p-4">
                <img src={it.image} alt={it.style_name} className="h-20 w-20 rounded-xl object-cover" />
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <h3 className="font-display text-lg font-bold text-sugar-ink">{it.style_name}</h3>
                    <button data-testid={`cart-remove-${i}`} onClick={() => cart.removeItem(i)} className="text-sugar-muted hover:text-red-500"><Trash2 size={17} /></button>
                  </div>
                  <p className="mt-1 line-clamp-2 text-xs text-sugar-ink2">
                    {Object.entries(it.selections).filter(([k, v]) => v && (!Array.isArray(v) || v.length) && !(typeof v === "string" && v.startsWith("/api/"))).map(([k, v]) => `${Array.isArray(v) ? v.join(", ") : v}`).join(" · ")}
                  </p>
                  <div className="mt-1 font-bold text-sugar-pink">{money(it.unit_price)}</div>
                  <CartPhotos rows={detailRows(it)} idx={i} />
                </div>
              </div>
            ))}
          </div>

          {/* fulfillment */}
          {dateBlocked && (
            <div data-testid="checkout-date-blocked" className="mt-6 flex items-center gap-3 rounded-xl border-2 border-red-300 bg-red-50 p-4 text-sm font-semibold text-red-700">
              <CalendarX2 size={18} className="shrink-0" /> Sorry, {cart.date} is unavailable for pickup or delivery. Please go back and choose another date.
            </div>
          )}
          <h2 className="mt-8 font-display text-xl font-bold text-sugar-ink">{isQuick ? "Pickup" : "Delivery or pickup"}</h2>
          {isQuick ? (
            <>
              <div data-testid="pickup-only-notice" className="mt-3 flex items-center gap-3 rounded-xl border border-sugar-border bg-sugar-blush p-4 text-sm font-semibold text-sugar-ink">
                <Store size={18} className="shrink-0 text-sugar-pink" /> Same-day & next-day orders are <span className="text-sugar-pink">pickup only</span> in Langley.
              </div>
              {isSameDay && (
                <div className="mt-4">
                  <label className="mb-1.5 block text-sm font-bold text-sugar-ink"><Clock size={15} className="mr-1 inline" /> Pickup time (today)</label>
                  {slots.length === 0 ? (
                    <p data-testid="no-slots" className="rounded-xl border border-sugar-border bg-white p-4 text-sm text-sugar-ink2">No same-day pickup slots left today — please choose Next-Day or a Pre-Order date.</p>
                  ) : (
                    <>
                      <div className="flex flex-wrap gap-2">
                        {slots.map((s) => (
                          <button key={s.v} type="button" data-testid={`pickup-slot-${s.v}`} onClick={() => setPickupTime(s.v)} className={`rounded-full border px-3.5 py-2 text-sm font-semibold ${pickupTime === s.v ? "border-sugar-pink bg-sugar-pink text-white" : "border-sugar-border bg-white text-sugar-ink2 hover:border-sugar-pink/50"}`}>
                            {s.label}{s.rush ? " ⚡" : ""}
                          </button>
                        ))}
                      </div>
                      <p className="mt-2 text-xs text-sugar-ink2">Pickups need at least 2 hours notice. ⚡ slots (under 6 hours) add a {money(orderInfo?.rush_fee || 50)} rush fee.</p>
                    </>
                  )}
                </div>
              )}
              {cart.mode === "next_day" && <PickupSlotPicker times={pickupTimes} value={pickupTime} onChange={setPickupTime} />}
            </>
          ) : (
            <>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <button data-testid="method-delivery" onClick={() => setMethod("delivery")} className={`flex items-center gap-2 rounded-xl border p-4 text-sm font-bold ${method === "delivery" ? "border-sugar-pink bg-sugar-blush text-sugar-ink" : "border-sugar-border bg-white text-sugar-ink2"}`}>
                  <Truck size={18} /> Delivery
                </button>
                <button data-testid="method-pickup" onClick={() => setMethod("pickup")} className={`flex items-center gap-2 rounded-xl border p-4 text-sm font-bold ${method === "pickup" ? "border-sugar-pink bg-sugar-blush text-sugar-ink" : "border-sugar-border bg-white text-sugar-ink2"}`}>
                  <Store size={18} /> Pickup (Langley)
                </button>
              </div>

              {method === "delivery" && (
                <div className="mt-4">
                  <label className="mb-1.5 block text-sm font-bold text-sugar-ink">Delivery area</label>
                  <div className="flex flex-wrap gap-2">
                    {zones.filter((z) => z.id !== "pickup").map((z) => (
                      <button key={z.id} data-testid={`zone-${z.id}`} onClick={() => setZone(z.id)} className={`rounded-full border px-4 py-2 text-sm font-semibold ${zone === z.id ? "border-sugar-pink bg-sugar-pink text-white" : "border-sugar-border bg-white text-sugar-ink2 hover:border-sugar-pink/50"}`}>
                        {z.label}{!["langley", "surrey"].includes(z.id) && z.fee > 0 ? ` (${money(z.fee)})` : ""}
                      </button>
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-sugar-ink2">Free in Langley & Surrey, and on all orders over {money(freeThreshold)}.</p>
                </div>
              )}

              {needsPickupSlots && <PickupSlotPicker times={pickupTimes} value={pickupTime} onChange={setPickupTime} />}
            </>
          )}

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <div><label className="mb-1.5 block text-sm font-bold text-sugar-ink">Your name</label><Input data-testid="checkout-name" value={form.customer_name} onChange={set("customer_name")} className="rounded-xl border-sugar-border" /></div>
            <div><label className="mb-1.5 block text-sm font-bold text-sugar-ink">Phone</label><Input data-testid="checkout-phone" value={form.customer_phone} onChange={set("customer_phone")} className="rounded-xl border-sugar-border" /></div>
          </div>
          {method === "delivery" && (
            <div className="mt-4"><label className="mb-1.5 block text-sm font-bold text-sugar-ink">Full delivery address</label><Input data-testid="checkout-address" value={form.address} onChange={set("address")} className="rounded-xl border-sugar-border" /></div>
          )}
          {requireDeliveryTime && (
            <div className="mt-4">
              <label className="mb-1.5 block text-sm font-bold text-sugar-ink"><Clock size={15} className="mr-1 inline" /> Delivery window (required)</label>
              <div className="flex flex-wrap gap-2">
                {DELIVERY_WINDOWS.map((w) => (
                  <button key={w} type="button" data-testid={`delivery-window-${w.includes("AM") ? "am" : "pm"}`} onClick={() => setDeliveryTime(w)} className={`rounded-full border px-4 py-2 text-sm font-semibold ${deliveryTime === w ? "border-sugar-pink bg-sugar-pink text-white" : "border-sugar-border bg-white text-sugar-ink2 hover:border-sugar-pink/50"}`}>
                    {w}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-xs text-sugar-ink2">Deliveries go out 8–10 AM or 8–10 PM.</p>
              <label data-testid="delivery-policy" className={`mt-4 flex cursor-pointer items-start gap-3 rounded-xl border-2 p-4 text-sm ${agreePolicy ? "border-sugar-pink bg-sugar-blush" : "border-sugar-border bg-white"}`}>
                <input type="checkbox" data-testid="delivery-policy-checkbox" checked={agreePolicy} onChange={(e) => setAgreePolicy(e.target.checked)} className="mt-0.5 h-5 w-5 shrink-0 accent-[#D98AA6]" />
                <span className="font-medium text-sugar-ink">{DELIVERY_POLICY} <span className="text-sugar-pink">*</span></span>
              </label>
            </div>
          )}
          <div className="mt-4"><label className="mb-1.5 block text-sm font-bold text-sugar-ink">Notes (optional)</label><Textarea data-testid="checkout-notes" value={form.notes} onChange={set("notes")} className="rounded-xl border-sugar-border" /></div>
          <TipSelector tip={tip} setTip={setTip} subtotal={subtotal} />
        </div>

        {/* summary */}
        <div className="lg:sticky lg:top-24 lg:h-fit">
          <div className="rounded-2xl border border-sugar-border bg-white p-6 sugar-card-shadow">
            <h3 className="font-display text-xl font-bold text-sugar-ink">Order summary</h3>
            <p className="mt-0.5 text-xs text-sugar-muted">{MODE_META[cart.mode]?.label} · {cart.date}</p>

            {/* itemized visual breakdown */}
            <div data-testid="summary-items" className="mt-4 space-y-3">
              {cart.items.map((item, i) => {
                const rows = detailRows(item);
                const photo = rows.find((r) => r.image);
                return (
                  <div key={i} data-testid={`summary-item-${i}`} className="rounded-xl border border-sugar-border bg-sugar-bg/60 p-3">
                    <div className="flex items-start gap-3">
                      <img src={photo ? photo.image : item.image} alt={item.style_name} className="h-14 w-14 shrink-0 rounded-lg border border-sugar-border object-cover" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <span className="font-display text-[15px] font-bold leading-tight text-sugar-ink">{item.style_name}</span>
                          <span className="shrink-0 font-bold text-sugar-pink">{money(item.unit_price)}</span>
                        </div>
                        <ul className="mt-2 space-y-1">
                          {rows.filter((r) => !r.image && !r.addon).map((r, j) => (
                            <li key={j} className="flex justify-between gap-3 text-[11px]">
                              <span className="text-sugar-muted">{r.label}</span>
                              <span className="text-right font-semibold text-sugar-ink2">{r.value}</span>
                            </li>
                          ))}
                        </ul>
                        {rows.some((r) => r.addon) && (
                          <div className="mt-2 border-t border-sugar-border/70 pt-2">
                            <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-sugar-rose">Add-ons</p>
                            <ul className="space-y-1">
                              {rows.filter((r) => r.addon).map((r, j) => (
                                <li key={j} className="text-[11px] font-semibold text-sugar-ink2">+ {r.value}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {photo && <p className="mt-2 text-[10px] font-semibold text-sugar-rose">📎 Inspiration photo attached</p>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="my-4 h-px bg-sugar-border" />
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-sugar-ink2"><span>Subtotal</span><span data-testid="summary-subtotal" className="font-semibold text-sugar-ink">{money(subtotal)}</span></div>
              {!isQuick && (
                <div className="flex justify-between text-sugar-ink2"><span>Delivery</span><span data-testid="summary-delivery" className="font-semibold text-sugar-ink">{deliveryFee === 0 ? "FREE" : money(deliveryFee)}</span></div>
              )}
              {rushFee > 0 && (
                <div className="flex justify-between text-sugar-ink2"><span>Rush fee (under 6 hrs)</span><span data-testid="summary-rush" className="font-semibold text-sugar-ink">{money(rushFee)}</span></div>
              )}
              <div className="flex justify-between text-sugar-ink2"><span>GST + PST (12%)</span><span data-testid="summary-tax" className="font-semibold text-sugar-ink">{money(tax)}</span></div>
              {tipValue > 0 && (
                <div className="flex justify-between text-sugar-ink2"><span>Tip{tip.type === "percent" ? ` (${tip.pct}%)` : ""}</span><span data-testid="summary-tip" className="font-semibold text-sugar-ink">{money(tipValue)}</span></div>
              )}
            </div>
            <div className="my-4 h-px bg-sugar-border" />
            <div className="flex items-center justify-between">
              <span className="font-display text-lg font-bold text-sugar-ink">Total</span>
              <span data-testid="summary-total" className="font-display text-3xl font-extrabold text-sugar-pink">{money(total)}</span>
            </div>
            <Button data-testid="place-order-btn" disabled={loading || dateBlocked} onClick={placeOrder} className="mt-5 w-full rounded-full bg-sugar-pink py-6 text-base font-bold text-white hover:bg-sugar-pinkhover disabled:opacity-50">
              {loading ? "Redirecting to payment…" : <>Pay {money(total)} <ArrowRight size={18} className="ml-2" /></>}
            </Button>
            <p className="mt-3 text-center text-xs text-sugar-muted">Secure checkout via Stripe · test card 4242 4242 4242 4242</p>
          </div>
        </div>
      </div>
    </div>
  );
}
